import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { QRCodeCanvas } from "qrcode.react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ArrowRight, ShieldCheck, BadgeCheck, Share2, X, Info, Copy } from "lucide-react";
import { TIER_SHORT, tierBadgeClass } from "@/lib/tiers";

const STATUS = {
  pending: { label: "Pending", cls: "bg-[#F9F3E8] text-[#B07B2E] border-[#D4A373]/30" },
  sealed: { label: "Sealed", cls: "bg-[#EDEBF5] text-[#4B4E8C] border-[#4B4E8C]/20" },
  awaiting_outcome: { label: "Awaiting Outcome", cls: "bg-[#F9F3E8] text-[#B07B2E] border-[#D4A373]/30" },
  disputed: { label: "Disputed", cls: "bg-[#FBEEE6] text-[#B5541F] border-[#B5541F]/25" },
  honored: { label: "Honored", cls: "bg-[#E6F0EB] text-[#2E6B4F] border-[#2E6B4F]/20" },
  broken: { label: "Broken", cls: "bg-[#F5E6E6] text-[#9C3A3A] border-[#9C3A3A]/20" },
  unresolved: { label: "Unresolved", cls: "bg-[#F1EFEA] text-[#7A6E52] border-[#B9A88A]/40" },
  declined: { label: "Declined", cls: "bg-[#EFEDEA] text-[#8A877F] border-[#DEDBD3]" },
};

export function StatusBadge({ status }) {
  const s = STATUS[status] || STATUS.pending;
  return (
    <span data-testid={`status-badge-${status}`} className={`overline px-2.5 py-1 rounded border ${s.cls}`}>
      {s.label}
    </span>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="bg-white border border-[#DEDBD3] rounded-lg p-6">
      <p className="overline text-[#5C5A54]">{label}</p>
      <p className={`font-serif-dl text-4xl mt-2 ${accent || "text-[#1A1A1A]"}`}>{value}</p>
    </div>
  );
}

function myOutcome(d, email) {
  const po = d.party_outcomes || {};
  if (po[email]) return po[email];
  if (["honored", "broken", "unresolved"].includes(d.status)) return d.status;
  return d.status;
}

export default function Dashboard() {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [daps, setDaps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showTip, setShowTip] = useState(false);
  const [share, setShare] = useState(null);

  const tier = user?.identity_verification?.verification_tier || "unverified";

  useEffect(() => {
    (async () => {
      try {
        const [p, d] = await Promise.all([api.get("/profile/me"), api.get("/daps")]);
        setProfile(p.data);
        setDaps(d.data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const actionNeeded = daps.filter(
    (d) => d.status === "pending" && d.counterparty_email === user.email
  );

  const shareUrl = share ? `${window.location.origin}/s/${share.token}` : "";

  const createShare = async () => {
    try {
      const r = await api.post("/share/create");
      setShare(r.data);
    } catch (e) {
      toast.error("Could not create a share link");
    }
  };

  return (
    <div data-testid="dashboard-page" className="space-y-10">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="overline text-[#E07A5F]">Welcome back</p>
          <h1 className="font-serif-dl text-4xl sm:text-5xl tracking-tight mt-1 flex items-center gap-3">
            {user.name}
            <span
              data-testid="verification-badge"
              className={`overline px-2.5 py-1 rounded border inline-flex items-center gap-1 ${tierBadgeClass(tier)}`}
            >
              {tier === "license" && <BadgeCheck size={12} />}
              {TIER_SHORT[tier]}
            </span>
          </h1>
        </div>
        <button
          data-testid="share-standing-btn"
          onClick={createShare}
          className="flex items-center gap-2 border border-[#1B4D3E] text-[#1B4D3E] rounded-md px-4 py-2.5 text-sm font-medium hover:bg-[#F0EBE1] transition-colors"
        >
          <Share2 size={16} /> Share my standing
        </button>
      </div>

      {/* Trust profile */}
      <section className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <div className="lg:col-span-1 bg-[#1B4D3E] text-white rounded-lg p-8 flex flex-col justify-between relative">
          <div className="flex items-center justify-between text-white/80">
            <div className="flex items-center gap-2">
              <ShieldCheck size={16} />
              <span className="overline">Honor Standing</span>
            </div>
            <button
              data-testid="standing-info-btn"
              onMouseEnter={() => setShowTip(true)}
              onMouseLeave={() => setShowTip(false)}
              onClick={() => setShowTip((v) => !v)}
              className="text-white/70 hover:text-white"
            >
              <Info size={15} />
            </button>
          </div>
          {showTip && (
            <div data-testid="standing-tooltip" className="absolute z-10 top-14 left-6 right-6 bg-white text-[#1A1A1A] text-xs rounded-lg p-3 shadow-lg border border-[#DEDBD3] leading-relaxed">
              Standing reflects daps honored. Disputed daps aren't counted against you until
              resolved; unresolved disputes weigh less than confirmed breaks.
            </div>
          )}
          <div>
            <p className="font-serif-dl text-6xl leading-none mt-6" data-testid="honor-score">
              {profile ? `${profile.honor_score}%` : "—"}
            </p>
            <p className="text-white/70 text-sm mt-2">
              Private by default. Yours to share — honor, never a public credit score.
            </p>
          </div>
        </div>
        <div className="lg:col-span-3 grid grid-cols-2 sm:grid-cols-4 gap-6">
          <Stat label="Sealed" value={profile?.sealed ?? "—"} accent="text-[#4B4E8C]" />
          <Stat label="Honored" value={profile?.honored ?? "—"} accent="text-[#2E6B4F]" />
          <Stat label="Broken" value={profile?.broken ?? "—"} accent="text-[#9C3A3A]" />
          <Stat label="Unresolved" value={profile?.unresolved ?? "—"} accent="text-[#7A6E52]" />
        </div>
      </section>

      {/* Action needed */}
      {actionNeeded.length > 0 && (
        <section>
          <h2 className="font-serif-dl text-2xl tracking-tight mb-4">Awaiting your seal</h2>
          <div className="flex flex-col gap-3">
            {actionNeeded.map((d) => (
              <Link
                key={d.dap_id}
                to={`/dap/${d.dap_id}`}
                data-testid={`action-dap-${d.dap_id}`}
                className="bg-[#F9F3E8] border border-[#D4A373]/40 rounded-lg p-5 flex items-center justify-between hover:border-[#D4A373] transition-colors"
              >
                <div>
                  <p className="font-medium">{d.title}</p>
                  <p className="text-sm text-[#5C5A54]">from {d.creator_name || d.creator_phone_mask || d.creator_email}</p>
                </div>
                <span className="flex items-center gap-2 text-[#B07B2E] font-medium text-sm">
                  Review & seal <ArrowRight size={16} />
                </span>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* All daps */}
      <section>
        <h2 className="font-serif-dl text-2xl tracking-tight mb-4">Your Daps</h2>
        {loading ? (
          <p className="text-[#5C5A54]">Loading…</p>
        ) : daps.length === 0 ? (
          <div className="bg-white border border-dashed border-[#DEDBD3] rounded-lg p-12 text-center">
            <p className="font-serif-dl text-2xl text-[#5C5A54]">No daps yet.</p>
            <p className="text-[#5C5A54] mt-1">Start one and seal your word.</p>
            <Link
              to="/create"
              data-testid="empty-create-btn"
              className="inline-block mt-6 bg-[#1B4D3E] text-white rounded-md px-6 py-3 font-serif-dl tracking-wide hover:bg-[#143A2F] transition-colors"
            >
              Create your first Dap
            </Link>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {daps.map((d) => (
              <Link
                key={d.dap_id}
                to={`/dap/${d.dap_id}`}
                data-testid={`dap-row-${d.dap_id}`}
                className="bg-white border border-[#DEDBD3] rounded-lg p-5 flex items-center justify-between hover:shadow-md transition-shadow"
              >
                <div className="min-w-0">
                  <p className="font-medium truncate flex items-center gap-2">
                    {d.title}
                    {d.is_demo && (
                      <span className="overline px-1.5 py-0.5 rounded bg-[#F0EBE1] text-[#8A877F] border border-[#DEDBD3]">DEMO</span>
                    )}
                  </p>
                  <p className="text-sm text-[#5C5A54] truncate">
                    {d.mediator_email === user.email &&
                    d.creator_email !== user.email &&
                    d.counterparty_email !== user.email
                      ? `mediating · ${d.creator_phone_mask || d.creator_email} ⟷ ${d.counterparty_phone_mask || d.counterparty_email}`
                      : d.creator_email === user.email
                      ? `with ${d.counterparty_name || d.counterparty_phone_mask || d.counterparty_email}`
                      : `from ${d.creator_name || d.creator_phone_mask || d.creator_email}`}
                  </p>
                </div>
                <div className="flex items-center gap-4 shrink-0">
                  <StatusBadge status={myOutcome(d, user.email)} />
                  <ArrowRight size={16} className="text-[#A3A095]" />
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>

      {/* Share modal */}
      {share && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm p-4" data-testid="share-modal">
          <div className="bg-white rounded-2xl border border-[#DEDBD3] w-full max-w-md p-8 text-center animate-fade-up relative">
            <button onClick={() => setShare(null)} data-testid="share-close" className="absolute top-4 right-4 text-[#A3A095] hover:text-[#1A1A1A]">
              <X size={20} />
            </button>
            <p className="overline text-[#E07A5F]">Share my standing</p>
            <h3 className="font-serif-dl text-3xl tracking-tight mt-1">Present your credential</h3>
            <p className="text-sm text-[#5C5A54] mt-2">
              Shows only your name, badge, standing and totals — never your dap details.
              Expires in 72 hours; revoke anytime in Settings.
            </p>
            <div className="mt-6 flex justify-center bg-white p-4 rounded-xl border border-[#DEDBD3]">
              <QRCodeCanvas value={shareUrl} size={168} fgColor="#1B4D3E" data-testid="share-qr" />
            </div>
            <div className="mt-4 flex items-center gap-2 bg-[#F9F8F6] border border-[#DEDBD3] rounded-md px-3 py-2">
              <input
                readOnly
                value={shareUrl}
                data-testid="share-url"
                className="flex-1 bg-transparent text-xs text-[#5C5A54] outline-none truncate"
              />
              <button
                data-testid="copy-share-btn"
                onClick={() => { navigator.clipboard?.writeText(shareUrl); toast.success("Link copied"); }}
                className="text-[#1B4D3E] hover:text-[#143A2F]"
              >
                <Copy size={16} />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
